import React from 'react';
import ProductionEstimateCard from './ProductionEstimateCard';
import { ProductEstimate } from '../../../utils/productionEstimates';
import './ProductionEstimates.css';

export interface ProductionEstimatesData {
  kitchen_id?: number;
  warehouse_id?: number;
  warehouse_name?: string;
  window_minutes: number | string;
  target_time: string;
  target_time_formatted?: string;
  generated_at: string;
  estimates: ProductEstimate[];
  summary?: {
    total_products: number;
    total_cooking_time: number;
    earliest_start_time: string;
  };
}

interface Props {
  data: ProductionEstimatesData | null;
  loading: boolean;
  error: string | null;
  lastUpdate: Date;
  onRefresh: () => void;
}

const ProductionEstimates: React.FC<Props> = ({ data, loading, error, lastUpdate, onRefresh }) => {
  const formatLastUpdate = () => {
    return lastUpdate.toLocaleTimeString('es-MX', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (loading && !data) {
    return (
      <div className="production-estimates-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando estimaciones de producción...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="production-estimates-container">
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h3>Error al cargar estimaciones</h3>
          <p>{error}</p>
          <button className="retry-button" onClick={onRefresh}>
            Intentar de nuevo
          </button>
        </div>
      </div>
    );
  }

  if (!data || data.estimates.length === 0) {
    return (
      <div className="production-estimates-container">
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h3>No hay estimaciones disponibles</h3>
          <p>No hay estimaciones de producción para los próximos {data?.window_minutes || 60} minutos.</p>
          <p className="empty-hint">
            Las estimaciones se generan automáticamente basándose en datos históricos de ventas.
          </p>
          <button className="retry-button" onClick={onRefresh}>
            Actualizar
          </button>
        </div>
      </div>
    );
  }

  const totalProducts = data.summary?.total_products ?? data.estimates.length;
  const totalCookingTime = data.summary?.total_cooking_time ??
    data.estimates.reduce((sum, est) => sum + (est.cooking_time || est.cooking_time_minutes || 0), 0);

  return (
    <div className="production-estimates-container">
      <div className="estimates-header">
        <div className="header-left">
          <span className="header-title">📊 Producción Anticipada</span>
          <span className="header-subtitle">
            {data.warehouse_name || `Cocina #${data.warehouse_id ?? data.kitchen_id}`} · {data.window_minutes} min
          </span>
        </div>
        <div className="header-right">
          <span className="summary-pill">🍖 <strong>{totalProducts}</strong> productos</span>
          <span className="summary-pill">⏱️ <strong>{totalCookingTime}</strong> min cocción</span>
          <span className="meta-update">🕐 {formatLastUpdate()}</span>
          <button className="refresh-button" onClick={onRefresh} disabled={loading}>
            {loading ? '⏳' : '🔄'}
          </button>
        </div>
      </div>

      <div className="estimates-grid">
        {data.estimates.map((estimate) => (
          <ProductionEstimateCard
            key={estimate.product_id}
            estimate={estimate}
          />
        ))}
      </div>
    </div>
  );
};

export default ProductionEstimates;
